/**
 * RolesTab Component
 *
 * Display user's assigned roles (read-only).
 * Features: view role names and descriptions.
 */

import { useState, useEffect } from 'react';
import { IconShield, IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Table,
  Badge,
  Text,
  EmptyState,
  Loader,
  Center,
  Alert,
  ScrollArea,
} from '../../../components/ui';
import { useUserService, useRoleService } from '@actschurch/ccms-sdk';
import type { Role } from '@actschurch/ccms-sdk';
import type { ProfileTabProps } from '../types';

export function RolesTab({ userId, user }: ProfileTabProps) {
  const userService = useUserService();
  const roleService = useRoleService();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch user roles with details
   */
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get role names assigned to user
      const roleNames = await userService.getUserRoles(userId);

      // Get all roles to match descriptions
      const allRoles = await roleService.getAll();

      // Filter to get only user's roles with full details
      const userRoles = allRoles.filter((role) =>
        roleNames.includes(role.name)
      );

      setRoles(userRoles);
    } catch (err: any) {
      setError(err.message || 'Failed to load roles');
      console.error('[RolesTab] Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchRoles();
    }
  }, [userId]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    fetchRoles();
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error">
        {error}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Assigned Roles
              </Text>
              <Text size="xs" c="dimmed">
                Roles determine your access level and permissions in the system
              </Text>
            </div>
            <Group gap="xs">
              <Badge size="lg" variant="light" color="blue" leftSection={<IconShield size={14} />}>
                {roles.length} {roles.length === 1 ? 'Role' : 'Roles'}
              </Badge>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {roles.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Role Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {roles.map((role) => (
                    <Table.Tr key={role.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconShield size={16} color="var(--mantine-color-blue-6)" />
                          <Text fw={500}>{role.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {role.description || 'No description available'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconShield size={64} />}
              title="No roles assigned"
              description="You don't have any roles assigned yet. Contact an administrator if you need access."
              size="md"
            />
          )}
        </Stack>
      </Card>

      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          Roles are assigned by administrators and cannot be modified by users. If you believe you need additional roles, please contact your system administrator.
        </Text>
      </Alert>
    </Stack>
  );
}
